import { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Brain, Bot, Wand2, Sparkles, Zap, Target, GitMerge, Layers } from 'lucide-react';
import gsap from 'gsap';

interface FeatureTourModalProps {
  onClose: () => void;
}

const TOUR_STEPS = [
  {
    icon: Brain,
    title: 'Deep Analysis',
    subtitle: 'See What Others Miss',
    description: 'Upload any image and get instant insights from 10+ AI models working together. Detailed reports in seconds, catching every detail humans overlook.',
    color: 'bg-brutal-yellow',
    borderColor: 'border-brutal-yellow',
    accentIcon: Target,
  },
  {
    icon: GitMerge,
    title: 'Mix & Orchestrate',
    subtitle: 'Combine Multiple AI Outputs',
    description: 'Chain different analyses together. Feed results from one model into another. Build complex pipelines that blend detection, classification, and generation into a single controlled flow.',
    color: 'bg-brutal-orange',
    borderColor: 'border-brutal-orange',
    accentIcon: Layers,
  },
  {
    icon: Bot,
    title: 'AI Agents',
    subtitle: 'Automate Everything',
    description: 'Build powerful workflows once, run them forever. Your AI team processes thousands of images automatically while you focus on decisions that matter.',
    color: 'bg-brutal-magenta',
    borderColor: 'border-brutal-magenta',
    accentIcon: Zap,
  },
  {
    icon: Wand2,
    title: 'Controlled Generation',
    subtitle: 'Direct AI Creativity',
    description: 'Guide generative AI with precision. Use analysis results to steer image generation, inpainting, and enhancement. You control the creative direction, AI executes.',
    color: 'bg-brutal-cyan',
    borderColor: 'border-brutal-cyan',
    accentIcon: Sparkles,
  },
];

export default function FeatureTourModal({ onClose }: FeatureTourModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  // Entrance animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3 }
      );
      gsap.fromTo(modalRef.current,
        { opacity: 0, y: 50, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'power3.out', delay: 0.1 }
      );
      gsap.fromTo(contentRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', delay: 0.3 }
      );
    });

    return () => ctx.revert();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowRight' && !isLastStep) {
        goToStep(currentStep + 1, 'next');
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        goToStep(currentStep - 1, 'prev');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, isLastStep, isFirstStep]);

  const goToStep = (newStep: number, direction: 'next' | 'prev') => {
    if (isAnimating) return;
    setIsAnimating(true);

    gsap.to(contentRef.current, {
      opacity: 0,
      x: direction === 'next' ? -30 : 30,
      duration: 0.2,
      ease: 'power2.in',
      onComplete: () => {
        setCurrentStep(newStep);
        gsap.fromTo(contentRef.current,
          { opacity: 0, x: direction === 'next' ? 30 : -30 },
          {
            opacity: 1,
            x: 0,
            duration: 0.3,
            ease: 'power2.out',
            onComplete: () => setIsAnimating(false)
          }
        );
      }
    });
  };

  const handleClose = () => {
    gsap.to(modalRef.current, {
      opacity: 0,
      y: 30,
      scale: 0.95,
      duration: 0.25,
      ease: 'power2.in'
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      delay: 0.1,
      onComplete: () => onClose()
    });
  };

  const handleGetStarted = () => {
    handleClose();
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-brutal-black/80 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div
        ref={modalRef}
        className="bg-brutal-white border-4 border-brutal-black w-full max-w-2xl relative"
        style={{ boxShadow: '8px 8px 0px 0px #000000' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b-3 border-brutal-black">
          <span className="text-xs font-bold uppercase tracking-wider text-brutal-black/50">
            Step {currentStep + 1} of {TOUR_STEPS.length}
          </span>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center border-2 border-brutal-black bg-brutal-white hover:bg-brutal-red hover:text-brutal-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-6 sm:p-8">
          {/* Visual placeholder area */}
          <div
            className={`relative h-40 sm:h-48 mb-6 border-3 border-brutal-black ${step.color} overflow-hidden`}
            style={{ boxShadow: '4px 4px 0px 0px #000000' }}
          >
            {/* Decorative elements */}
            <div className="absolute inset-0 flex items-center justify-center">
              <step.icon className="h-16 w-16 sm:h-20 sm:w-20 text-brutal-black/20" />
            </div>

            {/* Floating accent shapes */}
            <div className="absolute top-4 right-4 w-8 h-8 bg-brutal-black rotate-12" />
            <div className="absolute bottom-4 left-4 w-6 h-6 border-3 border-brutal-black bg-brutal-white -rotate-6" />
            <step.accentIcon className="absolute top-4 left-4 h-6 w-6 text-brutal-black/40" />

            {/* Grid pattern overlay */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: 'linear-gradient(90deg, #000 1px, transparent 1px), linear-gradient(#000 1px, transparent 1px)',
                backgroundSize: '20px 20px'
              }}
            />
          </div>

          {/* Icon and Title */}
          <div className="flex items-start gap-4 mb-4">
            <div
              className={`w-12 h-12 ${step.color} border-3 border-brutal-black flex items-center justify-center flex-shrink-0`}
              style={{ boxShadow: '3px 3px 0px 0px #000000' }}
            >
              <step.icon className="h-6 w-6 text-brutal-black" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-brutal-black">
                {step.title}
              </h3>
              <p className="text-sm font-bold uppercase text-brutal-black/50">
                {step.subtitle}
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm sm:text-base text-brutal-black/70 font-medium leading-relaxed mb-6">
            {step.description}
          </p>
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-t-3 border-brutal-black bg-brutal-black/5">
          {/* Prev Button */}
          <button
            onClick={() => goToStep(currentStep - 1, 'prev')}
            disabled={isFirstStep || isAnimating}
            className={`flex items-center gap-2 px-4 py-2 border-2 border-brutal-black font-bold text-sm uppercase transition-all
              ${isFirstStep
                ? 'opacity-30 cursor-not-allowed bg-brutal-white'
                : 'bg-brutal-white hover:bg-brutal-black hover:text-brutal-white'
              }`}
            style={{ boxShadow: isFirstStep ? 'none' : '3px 3px 0px 0px #000000' }}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </button>

          {/* Dot indicators */}
          <div className="flex items-center gap-2">
            {TOUR_STEPS.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (index !== currentStep && !isAnimating) {
                    goToStep(index, index > currentStep ? 'next' : 'prev');
                  }
                }}
                className={`w-3 h-3 border-2 border-brutal-black transition-all
                  ${index === currentStep
                    ? `${step.color}`
                    : 'bg-brutal-white hover:bg-brutal-black/20'
                  }`}
              />
            ))}
          </div>

          {/* Next/Get Started Button */}
          {isLastStep ? (
            <button
              onClick={handleGetStarted}
              disabled={isAnimating}
              className="flex items-center gap-2 px-4 py-2 border-2 border-brutal-black bg-brutal-yellow font-bold text-sm uppercase transition-all hover:bg-brutal-lime"
              style={{ boxShadow: '3px 3px 0px 0px #000000' }}
            >
              Get Started
              <Sparkles className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => goToStep(currentStep + 1, 'next')}
              disabled={isAnimating}
              className="flex items-center gap-2 px-4 py-2 border-2 border-brutal-black bg-brutal-black text-brutal-white font-bold text-sm uppercase transition-all hover:bg-brutal-yellow hover:text-brutal-black"
              style={{ boxShadow: '3px 3px 0px 0px #000000' }}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
